import React from "react";
import { Navigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";

import { useAuth } from "../../components/contexts/AuthContext";
import { UnderlineTabs } from "../Clients/metrics/UnderlineTabs";
import { VisibilityPanel } from "../Clients/metrics/VisibilityPanel";
import {
  CLIENT_RANGE_PRESETS,
  MetricsDateRangeBar,
  defaultDateSelection,
} from "../../components/MetricsDateRangeBar";
import { TrafficPanel } from "../Clients/metrics/TrafficPanel";
import {
  getClientIntegrations,
  hasAnyIntegration,
} from "../../utils/clientIntegrations";

const TAB_SUBTITLES = {
  visibilidad: "Posiciones y visibilidad orgánica de tu web",
  trafico: "Tráfico y usuarios de tu web",
};

/**
 * The client's own metrics: the same panels consultants see in the client
 * detail, read-only, and only the tabs whose integration is linked.
 */
export function ClientMetrics() {
  const { user } = useAuth();
  const { setPageContext, selectedFilter, clientIds } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  // One selection for every tab, edited from the page header.
  const [dates, setDates] = React.useState(() =>
    defaultDateSelection(CLIENT_RANGE_PRESETS)
  );

  const { clientId: flaggedClientId, flags } = getClientIntegrations(
    user,
    selectedFilter
  );
  const clientId = flaggedClientId ?? selectedFilter ?? clientIds?.[0] ?? null;

  const tabs = React.useMemo(() => {
    const items = [];
    if (flags.search_console || flags.seranking) {
      items.push({ value: "visibilidad", label: "Visibilidad" });
    }
    if (flags.ga4) {
      items.push({ value: "trafico", label: "Tráfico y usuarios" });
    }
    return items;
  }, [flags.search_console, flags.seranking, flags.ga4]);

  const tabParam = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.value === tabParam)
    ? tabParam
    : tabs[0]?.value;

  React.useEffect(() => {
    setPageContext?.({
      title: "Métricas",
      subtitle: TAB_SUBTITLES[activeTab],
      showTitle: true,
      rightContent: (
        <MetricsDateRangeBar
          value={dates}
          onChange={setDates}
          presets={CLIENT_RANGE_PRESETS}
        />
      ),
    });
  }, [activeTab, dates, setPageContext]);

  if (!hasAnyIntegration(flags) || !clientId) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleTabChange = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Stack spacing={2}>
      {tabs.length > 1 && (
        <UnderlineTabs tabs={tabs} value={activeTab} onChange={handleTabChange} />
      )}

      {activeTab === "visibilidad" && (
        <VisibilityPanel
          key={`visibility-${clientId}`}
          clientId={clientId}
          readOnly
          showSearchConsole={flags.search_console}
          showSeRanking={flags.seranking}
          dates={dates}
        />
      )}

      {activeTab === "trafico" && (
        <TrafficPanel
          key={`traffic-${clientId}`}
          clientId={clientId}
          readOnly
          dates={dates}
        />
      )}
    </Stack>
  );
}

export default ClientMetrics;
